import React, { useState, useRef, useEffect } from 'react';
import { askAi } from '../services/aiChatService';
import { submitFeedback } from '../services/feedbackService';

const AiChatWidget = ({ reportName, reportId, workspaceId, activeFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Merhaba! Rapor hakkında sorularınızı yanıtlayabilirim.' }
  ]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedbackState, setFeedbackState] = useState({});
  const [feedbackComments, setFeedbackComments] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const parseMessage = (text) => {
    const filterMatch = text.match(/\n\[Kullanılan filtreler: (.*)\]$/s);
    if (filterMatch) {
      return {
        mainText: text.replace(/\n\[Kullanılan filtreler:.*\]$/s, '').trim(),
        filters: filterMatch[1]
      };
    }
    return { mainText: text, filters: null };
  };

  // RESET FONKSİYONU
  const handleResetChat = () => {
    setConversationHistory([]);
    setMessages([{
      role: 'assistant',
      text: '✅ Konuşma geçmişi temizlendi. Yeni bir soru sorabilirsiniz.'
    }]);
    setFeedbackState({});
    setFeedbackComments({});
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const data = await askAi(
        userMessage,
        reportName || '',
        reportId || '',
        workspaceId || '',
        conversationHistory,
        activeFilters || ''
      );

      const assistantMessage = data.response;

      // RESET SİNYALİ KONTROLÜ
      if (data.clearHistory) {
        setConversationHistory([]);
        setMessages([{
          role: 'assistant',
          text: assistantMessage
        }]);
        setFeedbackState({});
        setFeedbackComments({});
      } else {
        // Normal mesaj ekleme
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: assistantMessage,
          userMessage: userMessage,
          conversationHistorySnapshot: JSON.stringify(conversationHistory),
          activeFiltersSnapshot: activeFilters || ''
        }]);

        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: userMessage },
          { role: 'assistant', content: assistantMessage }
        ]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Bir hata oluştu, lütfen tekrar deneyin.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedbackClick = (msgIndex) => {
    setFeedbackState(prev => ({ ...prev, [msgIndex]: 'open' }));
  };

  const handleFeedbackCancel = (msgIndex) => {
    setFeedbackState(prev => ({ ...prev, [msgIndex]: 'idle' }));
    setFeedbackComments(prev => ({ ...prev, [msgIndex]: '' }));
  };

  const handleFeedbackSubmit = async (msgIndex) => {
    const msg = messages[msgIndex];
    const { mainText } = parseMessage(msg.text);

    setFeedbackState(prev => ({ ...prev, [msgIndex]: 'sending' }));

    try {
      await submitFeedback({
        reportId: reportId || '',
        userMessage: msg.userMessage || '',
        aiResponse: mainText,
        daxQuery: null,
        daxResult: null,
        conversationHistory: msg.conversationHistorySnapshot || '',
        activeFilters: msg.activeFiltersSnapshot || '',
        userComment: feedbackComments[msgIndex] || ''
      });
      setFeedbackState(prev => ({ ...prev, [msgIndex]: 'sent' }));
    } catch (err) {
      setFeedbackState(prev => ({ ...prev, [msgIndex]: 'open' }));
      alert('Geri bildirim gönderilemedi, lütfen tekrar deneyin.');
    }
  };

  return (
    <div style={styles.container}>
      {isOpen && (
        <div style={styles.chatBox}>
          <div style={styles.header}>
            <span style={styles.headerTitle}>🤖 AI Asistan</span>
            {reportName && <span style={styles.reportName}>{reportName}</span>}
            
            {/* RESET BUTONU */}
            <button 
              onClick={handleResetChat} 
              style={styles.resetButton}
              title="Yeni Analiz Başlat"
            >
              🔄
            </button>
            
            <button onClick={() => setIsOpen(false)} style={styles.closeButton}>✕</button>
          </div>

          <div style={styles.messages}>
            {messages.map((msg, i) => {
              if (msg.role === 'assistant') {
                const { mainText, filters } = parseMessage(msg.text);
                const fbState = feedbackState[i] || 'idle';
                const isFirstMessage = i === 0;

                return (
                  <div key={i} style={{ ...styles.message, ...styles.assistantMessage }}>
                    <span>{mainText}</span>
                    {filters && (
                      <div style={styles.filterChip}>
                        🔍 {filters}
                      </div>
                    )}

                    {!isFirstMessage && (
                      <div style={styles.feedbackArea}>
                        {fbState === 'idle' && (
                          <button
                            onClick={() => handleFeedbackClick(i)}
                            style={styles.thumbDownBtn}
                            title="Bu cevap yanlış"
                          >
                            👎 Yanlış cevap
                          </button>
                        )}

                        {fbState === 'open' && (
                          <div style={styles.feedbackForm}>
                            <textarea
                              placeholder="Ne yanlış? (opsiyonel)"
                              value={feedbackComments[i] || ''}
                              onChange={e => setFeedbackComments(prev => ({ ...prev, [i]: e.target.value }))}
                              style={styles.feedbackInput}
                              rows={2}
                            />
                            <div style={styles.feedbackButtons}>
                              <button onClick={() => handleFeedbackSubmit(i)} style={styles.feedbackSendBtn}>
                                Gönder
                              </button>
                              <button onClick={() => handleFeedbackCancel(i)} style={styles.feedbackCancelBtn}>
                                İptal
                              </button>
                            </div>
                          </div>
                        )}

                        {fbState === 'sending' && (
                          <span style={styles.feedbackSent}>Gönderiliyor...</span>
                        )}

                        {fbState === 'sent' && (
                          <span style={styles.feedbackSent}>✓ Geri bildirim alındı, teşekkürler!</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div key={i} style={{ ...styles.message, ...styles.userMessage }}>
                  {msg.text}
                </div>
              );
            })}
            {loading && (
              <div style={{ ...styles.message, ...styles.assistantMessage }}>
                <span style={styles.typing}>● ● ●</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={styles.inputArea}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Sorunuzu yazın... (Enter ile gönderin)"
              style={styles.input}
              rows={2}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={styles.sendButton}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setIsOpen(!isOpen)} style={styles.toggleButton}>
        {isOpen ? '✕' : '🤖'}
      </button>
    </div>
  );
};

const styles = {
  container: { position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000 },
  chatBox: { position: 'absolute', bottom: '60px', right: '0', width: '360px', height: '480px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { backgroundColor: '#1a1a2e', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' },
  headerTitle: { color: 'white', fontWeight: '600', fontSize: '14px', flex: 1 },
  reportName: { color: '#94a3b8', fontSize: '11px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  
  // YENİ: Reset butonu stili
  resetButton: { 
    background: 'rgba(255, 255, 255, 0.1)', 
    border: '1px solid rgba(255, 255, 255, 0.2)', 
    color: 'white', 
    cursor: 'pointer', 
    fontSize: '14px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s',
    ':hover': {
      background: 'rgba(255, 255, 255, 0.2)'
    }
  },
  
  closeButton: { background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '14px' },
  messages: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  message: { padding: '8px 12px', borderRadius: '8px', fontSize: '13px', maxWidth: '85%', lineHeight: '1.5' },
  userMessage: { backgroundColor: '#1a1a2e', color: 'white', alignSelf: 'flex-end', borderBottomRightRadius: '2px' },
  assistantMessage: { backgroundColor: '#f1f5f9', color: '#1e293b', alignSelf: 'flex-start', borderBottomLeftRadius: '2px' },
  filterChip: { marginTop: '6px', fontSize: '11px', color: '#64748b', backgroundColor: '#e2e8f0', borderRadius: '4px', padding: '3px 8px', display: 'inline-block' },
  typing: { color: '#94a3b8', fontSize: '18px', letterSpacing: '4px' },
  inputArea: { padding: '12px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '8px', alignItems: 'flex-end' },
  input: { flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'none', fontFamily: 'Arial', outline: 'none' },
  sendButton: { padding: '8px 12px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', height: '36px' },
  toggleButton: { width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#1a1a2e', color: 'white', border: 'none', cursor: 'pointer', fontSize: '22px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  feedbackArea: { marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '6px' },
  thumbDownBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#94a3b8', padding: '2px 0' },
  feedbackForm: { display: 'flex', flexDirection: 'column', gap: '6px' },
  feedbackInput: { fontSize: '12px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'none', fontFamily: 'Arial', outline: 'none' },
  feedbackButtons: { display: 'flex', gap: '6px' },
  feedbackSendBtn: { flex: 1, padding: '4px 8px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  feedbackCancelBtn: { flex: 1, padding: '4px 8px', backgroundColor: '#e2e8f0', color: '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  feedbackSent: { fontSize: '11px', color: '#22c55e' },
};

export default AiChatWidget;